import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Logo } from './logo.js';
import loginArt from '../assets/login-art.webp';
import backdrop from '../assets/page-backdrop.webp';

/**
 * The signed-out screen, transcribed from the design.
 *
 * ── Why this is shared and the copy is not ──────────────────────────────────
 *
 * All three portals draw the identical shape and differ only in three strings -
 * a tag, a headline and a line of sub-copy. Those strings are the point: they
 * are how somebody who followed a link knows whether they are about to sign
 * into the customer portal or the production console, which matters because
 * the same staff account can sign into two of them.
 *
 * ── The layout ──────────────────────────────────────────────────────────────
 *
 * A 44% left column carrying the pitch over artwork, and the form on the right
 * (`minmax(380px,44%) 1fr`). The logo sits at the top of the column and the
 * headline (30px/-0.8px) at its foot, straight on the artwork over a fade, with
 * no card between: the design's frosted card covered the picture.
 *
 * The 380px minimum is load-bearing. Below it the headline wraps to five lines
 * and the panel stops reading as a column and starts reading as a mistake, so
 * the whole split collapses to a single column instead.
 *
 * ── The artwork ────────────────────────────────────────────────────────────
 *
 * The designer's molar render, under the brand wash and the foot fade from the
 * shared tokens (.auth-artwork-overlay, .auth-artwork-scrim) - those are what
 * make white text legible on a near-white image, so they are not decoration. The right column gets the same
 * pale backdrop the app uses, so signing in and landing on the dashboard feel
 * like the same surface.
 */
export interface AuthSplitProps {
  /**
   * ADMIN, PRODUCTION: which console this is, for staff who can sign into
   * more than one. Left out on the customer portal.
   */
  tag?: string;
  /** The pitch, on the left. One sentence. */
  headline: string;
  /** Under the headline. Who may sign in, and what is expected of them. */
  blurb: string;
  /** The form's own heading, on the right. */
  title: string;
  /** Under the form's heading. */
  subtitle?: string;
  /** The hostname, in the footer. Confirms which portal at a glance. */
  domain: string;
  children: ReactNode;
}

export function AuthSplit({
  tag,
  headline,
  blurb,
  title,
  subtitle,
  domain,
  children,
}: AuthSplitProps) {
  return (
    <div className="relative min-h-dvh bg-background lg:grid lg:grid-cols-[minmax(380px,44%)_1fr]">
      {/* Left: the pitch, over artwork. Hidden on narrow screens, where it
          would push the form below the fold for no benefit. No card: the logo
          at the top and the copy at the foot, so the artwork stays in view. */}
      <div className="relative hidden overflow-hidden bg-brand-800 lg:block">
        <img
          src={loginArt}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 size-full object-cover object-[50%_65%]"
        />
        <div className="auth-artwork-overlay absolute inset-0" aria-hidden="true" />
        <div className="auth-artwork-scrim absolute inset-0" aria-hidden="true" />

        <div className="relative flex min-h-full flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <span className="flex items-center rounded-lg bg-white px-3.5 py-2 shadow-sm">
              <Logo />
            </span>
            {tag && (
              <span className="rounded-md border border-auth-panel-border bg-auth-artwork-foreground/15 px-2.5 py-1 font-mono text-[11px] font-bold tracking-[0.08em] whitespace-nowrap text-auth-artwork-foreground backdrop-blur-sm">
                {tag}
              </span>
            )}
          </div>

          <div className="max-w-[420px]">
            <h2 className="mb-3.5 text-[30px] leading-[1.2] font-semibold tracking-[-0.8px] text-balance text-auth-artwork-foreground">
              {headline}
            </h2>
            <p className="text-sm leading-[1.65] text-pretty text-auth-artwork-muted">{blurb}</p>
          </div>
        </div>
      </div>

      {/* Right: the form, on the app's own backdrop. */}
      <div className="relative flex min-h-dvh flex-col justify-center px-6 py-10 lg:min-h-full">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat dark:hidden"
          style={{ backgroundImage: `url(${backdrop})` }}
        />

        <div className="relative mx-auto flex w-full max-w-[380px] flex-col">
          {/* The mark, for the narrow layout where the left panel is gone and
              nothing else says which product this is. */}
          <span className="mb-6 lg:hidden">
            <Logo />
          </span>

          <h1 className="text-[21px] font-semibold tracking-[-0.3px]">{title}</h1>
          {subtitle && (
            <p className="mt-2 mb-6 text-[13px] leading-[1.6] text-muted-foreground">{subtitle}</p>
          )}
          {!subtitle && <div className="mb-6" />}

          {children}

          <p className="mt-8 font-mono text-[11.5px] text-muted-foreground">{domain}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * The six-box code entry the design draws for the second factor.
 *
 * Exported separately because the MFA step replaces the form inside the same
 * split rather than being another screen - the pitch on the left does not
 * change just because a password was accepted.
 *
 * Not a controlled six-input widget: this renders the boxes and lets the caller
 * own the value, because paste, autofill and password managers all deliver six
 * digits to one field and a six-input version has to unpick that. The real
 * input is one field; these are its appearance.
 */
export function CodeBoxes({ value, length = 6 }: { value: string; length?: number }) {
  return (
    <div className="mb-5 flex gap-2.5" aria-hidden="true">
      {Array.from({ length }, (_, i) => (
        <span
          key={i}
          className={cn(
            'flex h-[52px] w-full items-center justify-center rounded-[9px] border-[1.5px] bg-card font-mono text-xl font-semibold',
            i === value.length
              ? 'border-brand-500 shadow-[0_0_0_3px_rgba(91,126,240,0.15)]'
              : 'border-input',
            value[i] ? 'text-primary' : 'text-transparent',
          )}
        >
          {value[i] ?? '·'}
        </span>
      ))}
    </div>
  );
}
